import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "v-work-schedule" }
const _hoisted_2 = { class: "header" }
const _hoisted_3 = { class: "header__filter" }
const _hoisted_4 = { class: "pill-toggle" }
const _hoisted_5 = { class: "navigation" }
const _hoisted_6 = { class: "time" }
const _hoisted_7 = { class: "body" }
const _hoisted_8 = {
  key: 0,
  class: "kpi"
}
const _hoisted_9 = {
  key: 0,
  class: "kpi"
}

import CommonTable from "@/components/common/CommonTable.vue";
import { computed, onMounted, ref, watch } from "vue";
import { ITableHeader } from "@/types/table";
import { useWorkScheduleStore } from "@/features/work-schedule/work-schedule.store";
import {
  IWorkSchedule,
  IWorkScheduleCell,
} from "@/features/work-schedule/work-schedule.types";
import CustomTab from "@/components/custom/CustomTab.vue";
import { ChevronLeft, ChevronRight, Check } from "utruckin-icons";
import { DateTime } from "luxon";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import { ITeam } from "@/features/teams/team.types";
import { IShiftTemplate } from "@/features/shifts/shifts.types";
import { useShiftStore } from "@/features/shifts/shifts.store";
import { useTeamStore } from "@/features/teams/team.store";


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkScheduleView',
  setup(__props) {

const workScheduleStore = useWorkScheduleStore();
const shiftStore = useShiftStore();
const teamStore = useTeamStore();

const schedule = ref<IWorkSchedule | null>(null);
const scheduleLoading = ref(false);
const activeTab = ref<number>(0);
const tabs = [
  { label: "Weekly", value: 0 },
  { label: "Monthly", value: 1 },
];

const withKpi = ref<boolean>(false);

const team = ref<string | null>(null);
const teams = ref<ITeam[]>([]);
const shift = ref<string | null>(null);
const shifts = ref<IShiftTemplate[]>([]);

const currentDate = ref(DateTime.now());
const rangeStart = computed(() => {
  return activeTab.value === 0
    ? currentDate.value.startOf("week")
    : currentDate.value.startOf("month");
});

const rangeEnd = computed(() => {
  return activeTab.value === 0
    ? currentDate.value.endOf("week")
    : currentDate.value.endOf("month");
});

const formattedRange = computed(() => {
  return `${rangeStart.value.toFormat(
    "dd.MM.yyyy"
  )} - ${rangeEnd.value.toFormat("dd.MM.yyyy")}`;
});

const previousWeek = () => {
  currentDate.value =
    activeTab.value === 0
      ? currentDate.value.minus({ weeks: 1 })
      : currentDate.value.minus({ months: 1 });
};

const nextWeek = () => {
  currentDate.value =
    activeTab.value === 0
      ? currentDate.value.plus({ weeks: 1 })
      : currentDate.value.plus({ months: 1 });
};

function toTitleCase(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const scheduleHeaders = computed<ITableHeader[]>(() => {
  const base: ITableHeader[] = [
    { name: "no", label: "No", width: "50px" },
    { name: "name", label: "Username", width: "180px" },
  ];
  const dayCols: ITableHeader[] = (schedule.value?.days || []).map((day) => ({
    name: `day_${day.date}`,
    label: `${DateTime.fromISO(day.date).toFormat("dd.MM")}<br>${toTitleCase(
      day.code
    )}`,
    width: "60px",
    type: "day",
  }));

  const all: ITableHeader[] = [{ name: "all", label: "All", width: "60px" }];
  return [...base, ...dayCols, ...all];
});

const teamItems = computed(() =>
  teams.value.map((t) => ({ value: t.id, name: t.name }))
);

const shiftItems = computed(() =>
  shifts.value.map((s) => ({ value: String(s.id), name: s.title }))
);

const scheduleGroups = computed(() => {
  if (!schedule.value) return [];
  return schedule.value.groups.map((group) => ({
    label: group.shift_name,
    sublabel: group.shift_time,
    count: group.users_count,
    items: group.users.map((user, idx) => {
      const row: Record<string, any> = {
        _idx: idx,
        _userId: user.user_id,
        no: idx + 1,
        name: user.name,
        phone_number: user.phone_number || "—",
        task_count: user.total_tasks,
        task_kpi: user.total_kpi,
      };
      (schedule.value?.days || []).forEach((day) => {
        const cell = user.cells.find((c) => c.date === day.date) || null;
        row[`day_${day.date}`] = cell;
      });
      return row;
    }),
  }));
});

const getCellText = (_cell: IWorkScheduleCell | null): string => {
  return "";
};

const getCellClass = (cell: IWorkScheduleCell | null): string => {
  if (!cell) return "";
  if (cell.is_absent) return "cell--absent";
  if (cell.is_day_off) return "cell--day-off";
  if (cell.worked_count > 0) return "cell--worked";
  return "";
};

onMounted(async () => {
  const shiftLoading = ref(false);
  const teamLoading = ref(false);

  workScheduleStore.customFilter.period = "week";
  workScheduleStore.customFilter.period_date =
    rangeStart.value.toFormat("yyyy-MM-dd");

  schedule.value = await workScheduleStore.getWorkSchedule(scheduleLoading);

  [shifts.value, teams.value] = await Promise.all([
    shiftStore.getShifts(shiftLoading),
    teamStore.getTeams(teamLoading),
  ]);

  if (workScheduleStore.customFilter.team_id) {
    team.value = workScheduleStore.customFilter.team_id;
  }

  if (workScheduleStore.customFilter.shift_id) {
    shift.value = workScheduleStore.customFilter.shift_id;
  }
});

watch(
  () => activeTab.value,
  async (val) => {
    workScheduleStore.customFilter.period = val == 0 ? "week" : "month";
    workScheduleStore.customFilter.period_date =
      rangeStart.value.toFormat("yyyy-MM-dd");

    schedule.value = await workScheduleStore.getWorkSchedule(scheduleLoading);
  }
);

watch(
  () => currentDate.value,
  async () => {
    workScheduleStore.customFilter.period =
      activeTab.value == 0 ? "week" : "month";
    workScheduleStore.customFilter.period_date =
      rangeStart.value.toFormat("yyyy-MM-dd");
    schedule.value = await workScheduleStore.getWorkSchedule(scheduleLoading);
  }
);

watch(
  () => shift.value,
  async (val) => {
    workScheduleStore.customFilter.shift_id = val;
    schedule.value = await workScheduleStore.getWorkSchedule(scheduleLoading);
  }
);

watch(
  () => team.value,
  async (val) => {
    workScheduleStore.customFilter.team_id = val;
    schedule.value = await workScheduleStore.getWorkSchedule(scheduleLoading);
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(CustomTab, {
        tabs: tabs,
        modelValue: activeTab.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTab).value = $event))
      }, null, 8, ["modelValue"]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("label", _hoisted_4, [
          _cache[4] || (_cache[4] = _createElementVNode("span", null, "KPI", -1)),
          _withDirectives(_createElementVNode("input", {
            type: "checkbox",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((withKpi).value = $event))
          }, null, 512), [
            [_vModelCheckbox, withKpi.value]
          ]),
          _createElementVNode("span", {
            class: _normalizeClass(["checkmark", withKpi.value ? 'active' : ''])
          }, [
            (withKpi.value)
              ? (_openBlock(), _createBlock(_unref(Check), {
                  key: 0,
                  size: 14
                }))
              : _createCommentVNode("", true)
          ], 2)
        ]),
        _createVNode(CommonDropdown, {
          modelValue: shift.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((shift).value = $event)),
          items: shiftItems.value,
          label: "Shift",
          clearable: ""
        }, null, 8, ["modelValue", "items"]),
        _createVNode(CommonDropdown, {
          modelValue: team.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((team).value = $event)),
          items: teamItems.value,
          label: "Team",
          clearable: ""
        }, null, 8, ["modelValue", "items"]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", {
            class: "left",
            onClick: previousWeek
          }, [
            _createVNode(_unref(ChevronLeft), { size: 20 })
          ]),
          _createElementVNode("div", _hoisted_6, _toDisplayString(formattedRange.value), 1),
          _createElementVNode("div", {
            class: "right",
            onClick: nextWeek
          }, [
            _createVNode(_unref(ChevronRight), { size: 20 })
          ])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createVNode(CommonTable, {
        headers: scheduleHeaders.value,
        groups: scheduleGroups.value,
        loading: scheduleLoading.value,
        "sticky-columns": 2
      }, _createSlots({
        no: _withCtx(({ index }) => [
          _createElementVNode("span", null, _toDisplayString(index + 1), 1)
        ]),
        all: _withCtx(({ item }) => [
          _createElementVNode("span", null, [
            _createTextVNode(_toDisplayString(item.task_count) + " ", 1),
            (withKpi.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(item.task_kpi), 1))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 2
      }, [
        _renderList(schedule.value?.days, (day) => {
          return {
            name: `day_${day.date}`,
            fn: _withCtx(({ item }) => [
              _createElementVNode("span", {
                class: _normalizeClass(['day-cell', getCellClass(item[`day_${day.date}`])])
              }, [
                _createTextVNode(_toDisplayString(item[`day_${day.date}`].worked_count) + " ", 1),
                (withKpi.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(item[`day_${day.date}`].total_kpi), 1))
                  : _createCommentVNode("", true)
              ], 2)
            ])
          }
        })
      ]), 1032, ["headers", "groups", "loading"])
    ])
  ]))
}
}

})